import { config } from './env';

// Extend window interface to support Google Analytics (gtag.js) properties
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

const GA_TRACKING_ID = config.analytics?.gaId;

/**
 * Initializes Google Analytics (GA4) dynamically.
 * Adds the gtag.js script and sets up the global gtag function.
 */
export const initGA = (): void => {
  if (!GA_TRACKING_ID) {
    console.log('Google Analytics: No tracking ID (VITE_GA4_ID) configured. Tracking is disabled.');
    return;
  }

  // Prevent multiple script injections
  if (document.getElementById('google-analytics-script')) {
    return;
  }

  try {
    // Create script element to load Google Analytics
    const script = document.createElement('script');
    script.id = 'google-analytics-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
    document.head.appendChild(script);

    // Initialize the dataLayer and gtag function
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };

    // Configure base tracking options
    window.gtag('js', new Date());
    window.gtag('config', GA_TRACKING_ID, {
      send_page_view: false, // Page views handled manually to support SPA router properly
    });

    console.log(`Google Analytics: Configured and initialized with ID ${GA_TRACKING_ID}`);
  } catch (error) {
    console.error('Google Analytics: Failed to initialize tracking script', error);
  }
};

/**
 * Tracks a page view event.
 * @param path The URL path (e.g. '/listings' or '/profile')
 * @param title The document/page title (optional)
 */
export const trackPageView = (path: string, title?: string): void => {
  if (!GA_TRACKING_ID || typeof window.gtag !== 'function') {
    return;
  }

  window.gtag('config', GA_TRACKING_ID, {
    page_path: path,
    page_title: title || document.title,
  });
};

/**
 * Tracks a custom event.
 * @param action The name of the event (e.g. 'ad_click', 'contact_submit')
 * @param category The event category (e.g. 'Ad Interaction', 'User Actions')
 * @param label A label describing the event (optional)
 * @param value A numeric value associated with the event (optional)
 */
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number
): void => {
  if (!GA_TRACKING_ID || typeof window.gtag !== 'function') {
    return;
  }

  window.gtag('event', action, {
    event_category: category,
    event_label: label,
    value: value,
  });
};
